/**
 * Utility functions for handling amount display and formatting
 */

/**
 * Truncates a numeric amount to a specified number of decimal places (NOT rounded)
 * This ensures consistency with backend truncation logic for payment amounts
 * 
 * @param amount - The amount to truncate (string or number)
 * @param decimals - Number of decimal places to keep (default: 2)
 * @returns The truncated amount as a string
 * 
 * @example
 * truncateAmount('11.509999', 2) // returns '11.50'
 * truncateAmount(11.509999, 2) // returns '11.50'
 * truncateAmount('11.999', 2) // returns '11.99'
 * truncateAmount('11', 2) // returns '11.00'
 */
export function truncateAmount(amount: string | number | undefined | null, decimals: number = 2): string {
  if (amount === undefined || amount === null || amount === '') {
    return decimals > 0 ? `0.${'0'.repeat(decimals)}` : '0';
  }

  // Convert to string and handle scientific notation
  let amountStr = typeof amount === 'number' ? amount.toString() : amount;
  
  // Handle scientific notation (e.g., 1.23e-5)
  if (amountStr.includes('e') || amountStr.includes('E')) {
    amountStr = parseFloat(amountStr).toFixed(decimals + 10); // Extra precision for truncation
  }

  // Remove any non-numeric characters except decimal point and minus sign
  amountStr = amountStr.replace(/[^0-9.-]/g, '');
  
  // Handle negative numbers
  const isNegative = amountStr.startsWith('-');
  if (isNegative) {
    amountStr = amountStr.substring(1);
  }

  // Split by decimal point
  const parts = amountStr.split('.');
  
  // If no decimal part, add zeros
  if (parts.length === 1) {
    const result = decimals > 0 ? `${parts[0]}.${'0'.repeat(decimals)}` : parts[0];
    return isNegative ? `-${result}` : result;
  }
  
  // Truncate decimal part (not round)
  const integerPart = parts[0] || '0';
  const decimalPart = parts[1] || '';
  
  // Truncate to specified decimals (pad with zeros if needed)
  const truncatedDecimal = decimalPart.substring(0, decimals).padEnd(decimals, '0');
  
  const result = decimals > 0 ? `${integerPart}.${truncatedDecimal}` : integerPart;
  return isNegative ? `-${result}` : result;
}

/**
 * Formats a payment amount for display with proper truncation
 * This is the standard way to display payment amounts in the UI
 * 
 * @param amount - The amount to format
 * @param decimals - Number of decimal places (default: 2)
 * @param includeCommas - Whether to include thousand separators (default: false)
 * @returns Formatted amount string
 */
export function formatPaymentAmount(
  amount: string | number | undefined | null,
  decimals: number = 2,
  includeCommas: boolean = false
): string {
  const truncated = truncateAmount(amount, decimals);
  
  if (!includeCommas) {
    return truncated;
  }
  
  // Add thousand separators
  const parts = truncated.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

/**
 * Compares two amounts after truncation to determine payment status
 * Used to check if an order is fully paid, underpaid, or overpaid
 * 
 * @param paidAmount - The amount actually paid
 * @param requiredAmount - The required amount
 * @param decimals - Number of decimal places for comparison (default: 2)
 * @returns 'underpaid' | 'fullpaid' | 'overpaid'
 */
export function comparePaymentAmounts(
  paidAmount: string | number | undefined | null,
  requiredAmount: string | number | undefined | null,
  decimals: number = 2
): 'underpaid' | 'fullpaid' | 'overpaid' {
  const paid = parseFloat(truncateAmount(paidAmount, decimals));
  const required = parseFloat(truncateAmount(requiredAmount, decimals));
  
  if (paid < required) {
    return 'underpaid';
  } else if (paid > required) {
    return 'overpaid';
  } else {
    return 'fullpaid';
  }
}

/**
 * Calculates the remaining amount to be paid (truncated, not rounded)
 * 
 * @param requiredAmount - The total required amount
 * @param paidAmount - The amount already paid
 * @param decimals - Number of decimal places (default: 2)
 * @returns The remaining amount as a string
 */
export function calculateRemainingAmount(
  requiredAmount: string | number | undefined | null,
  paidAmount: string | number | undefined | null,
  decimals: number = 2
): string {
  const required = parseFloat(truncateAmount(requiredAmount, decimals));
  const paid = parseFloat(truncateAmount(paidAmount, decimals));
  const remaining = Math.max(0, required - paid);
  
  return truncateAmount(remaining, decimals);
}

/**
 * Formats a token amount based on its standard decimals
 * USDT/USDC typically use 6 decimals on-chain but display with 2
 * 
 * @param amount - The amount to format (could be in smallest units)
 * @param tokenDecimals - The token's decimal places (e.g., 6 for USDT/USDC)
 * @param displayDecimals - How many decimals to show in UI (default: 2)
 * @returns Formatted amount string
 */
export function formatTokenAmount(
  amount: string | number | undefined | null,
  tokenDecimals: number,
  displayDecimals = 2
): string {
  if (tokenDecimals == null) {
    throw new Error('tokenDecimals is required to format token amounts');
  }
  if (!amount || amount === '0') return truncateAmount(0, displayDecimals);
  
  // If the amount is already a decimal (contains .), just truncate it
  const amountStr = amount.toString();
  if (amountStr.includes('.')) {
    return truncateAmount(amount, displayDecimals);
  }
  
  // Otherwise, convert from smallest units
  const divisor = Math.pow(10, tokenDecimals);
  const value = parseFloat(amountStr) / divisor;
  
  return truncateAmount(value, displayDecimals);
}
